import React, { useState, useEffect } from 'react';
import { Card, Button, Form } from 'react-bootstrap';
import Select from 'react-select';
import { Put, Get } from '../../utils/httpProvider';
import { ANNUAL_JOB_API, JOBGROUP_API } from '../../utils/apiUrl';
import { toast } from 'react-toastify';
import { useHistory, useParams, NavLink } from 'react-router-dom';
import { URI_INTERNAL } from '../../utils/pathLocations';

const AnnualJobCategoryEdit = () => {
    const history = useHistory();
    const { id } = useParams();
    const [jobGroups, setJobGroups] = useState([]);
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        unit: '',
        weight: 1,
        baseQuantity: 1,
        jobGroupId: '',
        isActive: true,
        note: ''
    });

    useEffect(() => {
        fetchJobGroups();
        if (id) {
            fetchJobDetail();
        }
    }, [id]);

    const fetchJobDetail = async () => {
        setLoading(true);
        try {
            const res = await Get(`${ANNUAL_JOB_API.GET_ITEM}/${id}`);
            if (res) {
                const job = res.data ?? res.Data ?? res;
                setFormData({
                    name: job.Name ?? job.name ?? '',
                    unit: job.Unit ?? job.unit ?? '',
                    weight: job.Weight ?? job.weight ?? 1,
                    baseQuantity: job.BaseQuantity ?? job.baseQuantity ?? 1,
                    jobGroupId: job.JobGroupId ?? job.jobGroupId ?? '',
                    isActive: (job.IsActive ?? job.isActive) !== false,
                    note: job.Note ?? job.note ?? ''
                });
            }
        } catch (error) {
            console.error('Error fetching job details:', error);
            toast.error('Không tải được thông tin công việc');
        } finally {
            setLoading(false);
        }
    };

    const fetchJobGroups = async () => {
        try {
            const res = await Get(JOBGROUP_API.GET_ALL, null, null);
            if (res) {
                const dataObj = res.data ?? res.Data ?? res;
                const rawList = Array.isArray(dataObj) ? dataObj : (dataObj.$values || []);
                const normalized = rawList.map(g => ({
                    id: g.Id ?? g.id,
                    name: g.Name ?? g.name
                }));
                setJobGroups(normalized);
            }
        } catch (error) {
            console.error('Error fetching job groups:', error);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            await Put(ANNUAL_JOB_API.UPDATE, id, formData);
            toast.success('Cập nhật thành công');
            history.push(URI_INTERNAL.ANNUAL_JOB_CATEGORY_PATH);
        } catch (error) {
            console.error('Error saving job:', error);
            toast.error('Có lỗi xảy ra');
        }
    };

    if (loading) return <div className="text-center p-5">Đang tải...</div>;

    return (
        <div className="container-fluid">
            <Card>
                <Card.Header>
                    <h3 className="card-title">Cập nhật công việc thường niên</h3>
                </Card.Header>
                <Card.Body>
                    <Form onSubmit={handleSubmit}>
                        <div className="row g-3">
                            <div className="col-md-12">
                                <Form.Group>
                                    <Form.Label>Tên công việc <span className="text-danger">*</span></Form.Label>
                                    <Form.Control
                                        type="text"
                                        required
                                        value={formData.name}
                                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                    />
                                </Form.Group>
                            </div>
                            <div className="col-md-6">
                                <Form.Group>
                                    <Form.Label>Nhóm công việc</Form.Label>
                                    <Select
                                        options={jobGroups.map(g => ({ value: g.id, label: g.name }))}
                                        value={jobGroups.map(g => ({ value: g.id, label: g.name })).find(o => o.value === formData.jobGroupId) || null}
                                        onChange={(selected) => setFormData({ ...formData, jobGroupId: selected ? selected.value : null })}
                                        placeholder="-- Chọn nhóm công việc --"
                                        isClearable
                                        isSearchable
                                    />
                                </Form.Group>
                            </div>
                            <div className="col-md-6">
                                <Form.Group>
                                    <Form.Label>Đơn vị tính</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={formData.unit}
                                        onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
                                    />
                                </Form.Group>
                            </div>
                            <div className="col-md-6">
                                <Form.Group>
                                    <Form.Label>Điểm</Form.Label>
                                    <Form.Control
                                        type="number"
                                        step="0.01"
                                        required
                                        value={formData.weight}
                                        onChange={(e) => setFormData({ ...formData, weight: e.target.value === '' ? '' : parseFloat(e.target.value) })}
                                    />
                                </Form.Group>
                            </div>
                            <div className="col-md-6">
                                <Form.Group>
                                    <Form.Label>Khối lượng chuẩn <span className="text-danger">*</span></Form.Label>
                                    <Form.Control
                                        type="number"
                                        step="0.01"
                                        required
                                        value={formData.baseQuantity}
                                        onChange={(e) => setFormData({ ...formData, baseQuantity: e.target.value === '' ? '' : parseFloat(e.target.value) })}
                                    />
                                    <Form.Text className="text-muted">
                                        Ví dụ: 20 bài báo = 1 điểm, thì Khối lượng chuẩn là 20.
                                    </Form.Text>
                                </Form.Group>
                            </div>
                            <div className="col-md-12">
                                <Form.Group>
                                    <Form.Label>Ghi chú</Form.Label>
                                    <Form.Control
                                        as="textarea"
                                        rows={3}
                                        value={formData.note}
                                        onChange={(e) => setFormData({ ...formData, note: e.target.value })}
                                    />
                                </Form.Group>
                            </div>
                            <div className="col-md-12">
                                <Form.Check
                                    type="switch"
                                    id="is-active-switch"
                                    label="Kích hoạt"
                                    checked={formData.isActive}
                                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                                />
                            </div>
                        </div>
                        <div className="mt-4 d-flex justify-content-end" style={{ gap: 8 }}>
                            <Button variant="primary" type="submit">Cập nhật</Button>
                            <NavLink to={URI_INTERNAL.ANNUAL_JOB_CATEGORY_PATH}>
                                <Button variant="secondary">Hủy bỏ</Button>
                            </NavLink>
                        </div>
                    </Form>
                </Card.Body>
            </Card>
        </div>
    );
};

export default AnnualJobCategoryEdit;
